import { FormHelperText, Stack, Typography } from '@mui/material';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { useAddPhotosMutation } from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import FormUpload from '../../../../shared/FormUpload';
import toast from '../../../../shared/Toast';
import { FormValues } from '../../../../types/experience';
import { useFormState } from '../useFormState';

const ExperiencePhotos = () => {
    const loggedInUser = UserRole();
    const { isSaveDisabled, setIsSaveDisabled } = useFormState();

    const [addPhoto] = useAddPhotosMutation();

    const { control, clearErrors, setError } = useFormContext<FormValues>();

    const photos = useWatch({ control, name: 'photos' });

    const handlePhotos = async (files: File[]) => {
        setIsSaveDisabled(true);
        try {
            const formData = new FormData();
            for (const image of files) {
                formData.append('files', image);
            }
            const requestInfo = [loggedInUser.company, formData];
            const response = await addPhoto(requestInfo).unwrap();
            clearErrors('photos');

            return response;
        } catch (error: any) {
            console.error('Photo upload error:', error);
            if (error.status === 401) {
                setError('photos', {
                    message:
                        'Authentication error. Please try logging in again.',
                });
                toast.error(
                    'Authentication error. Please try logging in again.',
                );
            } else {
                setError('photos', {
                    message: 'Error uploading files. Please try again.',
                });
                toast.error('Error uploading files. Please try again.');
            }
        } finally {
            setIsSaveDisabled(false);
        }
    };

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h2' component='legend'>
                        Photos
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }} gutterBottom>
                        Showcase your experience with up to 15 photos! We'll
                        need at least 5 photos to publish your experience.
                    </Typography>
                </div>
                <div>
                    <FormUpload
                        name='photos'
                        control={control}
                        onUpload={handlePhotos}
                        showFiles={true}
                        hasLightBox={true}
                        inputProps={{ 'aria-label': 'Upload photos' }}
                        validation={{
                            acceptedTypes: {
                                'image/*': [
                                    '.png',
                                    '.jpg',
                                    '.jpeg',
                                    '.gif',
                                    '.webp',
                                ],
                            },
                            maxFileSize: 5 * 1024 * 1024, // 5MB
                            maxFiles: 15,
                            resolution: {
                                width: 1024,
                                height: 683,
                            },
                            orientation: 'landscape',
                        }}
                        disabled={isSaveDisabled}
                        uploadText='Click to browse photos or drag and drop'
                    />
                    <FormHelperText>
                        {photos?.length < 5
                            ? 'Minimum 5 photos required'
                            : photos?.length === 10
                              ? 'Maximum uploads reached (15/15)'
                              : `${photos?.length || 0}/15 photos uploaded`}
                    </FormHelperText>
                </div>
            </Stack>
        </fieldset>
    );
};

export default ExperiencePhotos;
